import { JSX, ReactElement, useEffect, useState } from 'react';

import { Alert } from '../../components/alert';

import { hasOpenedPopover } from '../../utils/ui';

export const useCloseConfirmation = (props: UseCloseConfirmationPros) => {
  const { hostOpen, title, onClose, hasUnsavedChanges, message } = props;

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!hostOpen) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();

      if (isOpen) {
        return setIsOpen(false);
      }

      if (hasUnsavedChanges) {
        return setIsOpen(true);
      }

      onClose();
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hostOpen, isOpen, hasUnsavedChanges]);

  const showConfirmation = () => {
    if (hasUnsavedChanges && !hasOpenedPopover()) {
      setIsOpen(true);
    }
  };

  const renderAlert = (triggerElement?: ReactElement, openInModal = true) => {
    return (
      <Alert
        title={title}
        type="warning"
        message={message}
        icon="ai-brain-03"
        trigger={triggerElement}
        openInModal={openInModal}
        open={isOpen}
        popoverProps={{
          placement: 'left',
          offset: [0, 16],
          overflowPadding: 20,
        }}
        primaryButton={{
          onClick: (closeAlert) => {
            onClose();
            closeAlert?.();
          },
        }}
        onClose={() => setIsOpen(false)}
      />
    );
  };

  return {
    renderAlert,
    showConfirmation,
    isCloseConfirmationOpen: isOpen,
    setIsCloseConfirmationOpen: setIsOpen,
  };
};

interface UseCloseConfirmationPros {
  title: string;
  message: string;
  hostOpen: boolean;
  onClose: () => void;
  hasUnsavedChanges: boolean;
}

export type RenderAlert = (triggerElement?: ReactElement, openInModal?: boolean) => JSX.Element;
